import {
    ChartBarIcon,
    ChatBubbleOvalLeftIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ClipboardDocumentCheckIcon,
    CogIcon,
    DocumentIcon,
    InboxIcon,
    RectangleGroupIcon,
    ScaleIcon,
    TableCellsIcon,
    CalculatorIcon,
} from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { TooltipV2 } from '@cimplico/components';
import WorkpaperQuickNavigate from '../workpaper-quick-navigate/WorkpaperQuickNavigate';
import WorksheetSearch from '../worksheet-search/WorksheetSearch';

const tabs = [
    { name: 'Dashboard', href: 'dashboard', icon: ChartBarIcon },
    { name: 'Procedures', href: 'procedures', icon: ClipboardDocumentCheckIcon },
    { name: 'Trial Balance', href: 'trial-balance', icon: ScaleIcon },
    { name: 'Other Schedules', href: 'other-schedules', icon: RectangleGroupIcon },
    { name: 'Calculations', href: 'calculations', icon: CalculatorIcon },
    { name: 'Journals', href: 'journals', icon: TableCellsIcon },
    { name: 'Files', href: 'files', icon: DocumentIcon },
    { name: 'Internal Comments', href: 'internal-comments', icon: ChatBubbleOvalLeftIcon },
    { name: 'Client Queries', href: 'client-queries', icon: InboxIcon },
    { name: 'Settings', href: 'settings', icon: CogIcon },
];

function WorkpaperSidebar(): JSX.Element {
    const sidebarCollapsed = localStorage.getItem('WP.sidebar_collapsed');
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(!!(!sidebarCollapsed || sidebarCollapsed === 'false'));
    const [tab, setTab] = useState(matchRouteIndex(location.pathname));
    const { shortId } = useFirmContext();
    const { id: workpaperId } = useWorkpaperContext();

    /**
     * Finds the index of the tab that the provided path starts with.
     * @param path Path to match with tab routes
     * @returns Index of matched route or -1
     */
    function matchRouteIndex(path: string): number {
        const tabIndex = tabs.findIndex((tab) => path.includes(tab.href));

        // When tab.href is not found in any part of the path (index === -1), return 0
        // tabs[-1] will result to an undefined value
        return tabIndex === -1 ? 0 : tabIndex;
    }

    useEffect(() => {
        setTab(matchRouteIndex(location.pathname));
    }, [location.pathname]);

    const menuItemVariants = {
        initial: {
            opacity: 0,
        },
        opened: {
            opacity: 1,
            display: 'block',
            transition: {
                duration: 0.2,
                delay: 0.05,
                ease: 'linear',
            },
        },
        closed: {
            opacity: 0,
            transition: {
                duration: 0.1,
                ease: 'linear',
            },
            transitionEnd: {
                display: 'none',
            },
        },
    };

    const containerVariants = {
        opened: { width: '240px' },
        closed: { width: '65px' },
    };

    return (
        <AnimatePresence>
            <motion.aside
                className="text-ellipsis whitespace-nowrap h-full flex flex-col shrink-0 border-r border-gray-200 dark:border-white/[0.07] bg-white dark:bg-gray-800 px-4 justify-between"
                initial={!sidebarCollapsed || sidebarCollapsed === 'false' ? 'opened' : 'closed'}
                animate={sidebarOpen ? 'opened' : 'closed'}
                variants={containerVariants}
                transition={{
                    duration: 0.2,
                }}
                data-intercom-target="workpaper-sidebar"
            >
                <div>
                    <div
                        className="absolute flex mt-4 items-center justify-center hover:cursor-pointer"
                        data-intercom-target="workpaper-sidebar-workpaper-select"
                    >
                        <WorkpaperQuickNavigate isCollapsed={!sidebarOpen} />
                    </div>
                    <motion.div className="mt-16">
                        <div className="absolute" data-intercom-target="workpaper-sidebar-worksheet-search">
                            <WorksheetSearch
                                isCollapsed={!sidebarOpen}
                                onBlur={(): void => {
                                    if (localStorage.getItem('WP.sidebar_collapsed') === 'true') {
                                        setSidebarOpen(false);
                                    }
                                }}
                                onExpand={(): void => {
                                    setSidebarOpen(true);
                                }}
                            />
                        </div>
                    </motion.div>
                    <nav className="flex flex-col pt-12">
                        <ul className="mx-0 space-y-2">
                            {tabs
                                .map((item) => (
                                    <li key={item.name} data-intercom-target={`workpaper-sidebar-item-${item.href}`}>
                                        <Link to={{ pathname: `/${shortId}/workpapers/${workpaperId}/${item.href}` }}>
                                            <TooltipV2 message={item.name} disabled={sidebarCollapsed === 'false' || sidebarOpen}>
                                                <motion.div
                                                    className={`${
                                                        tabs[tab].name === item.name
                                                            ? 'bg-indigo-50 dark:bg-indigo-400/[0.2] text-indigo-600 dark:text-indigo-400'
                                                            : 'text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 hover:dark:bg-indigo-400/[0.2]'
                                                    } ${
                                                        sidebarOpen ? '' : 'w-8 h-8'
                                                    } flex items-center rounded-md py-2 pr-2 text-xs font-medium`}
                                                >
                                                    <div
                                                        className={`w-1 h-4 mr-1 shrink-0 ${
                                                            tabs[tab].name === item.name
                                                                ? 'bg-indigo-600 dark:bg-indigo-400'
                                                                : 'bg-transparent'
                                                        }`}
                                                    />
                                                    <item.icon className="h-4 w-4 shrink-0" />
                                                    <motion.div variants={menuItemVariants} className="ml-2">
                                                        {item.name}
                                                    </motion.div>
                                                </motion.div>
                                            </TooltipV2>
                                        </Link>
                                    </li>
                                ))}
                        </ul>
                    </nav>
                </div>
                <div className={`-mx-6 mt-auto flex items-center ${sidebarOpen ? 'justify-end' : 'justify-center'}`}>
                    <button
                        className="px-6 py-3 text-gray-500"
                        onClick={(): void => {
                            localStorage.setItem('WP.sidebar_collapsed', sidebarOpen ? 'true' : 'false');
                            setSidebarOpen(!sidebarOpen);
                        }}
                    >
                        {sidebarOpen ? <ChevronDoubleLeftIcon className="h-5 w-5" /> : <ChevronDoubleRightIcon className="h-5 w-5" />}
                    </button>
                </div>
            </motion.aside>
        </AnimatePresence>
    );
}

export default WorkpaperSidebar;
